/**
 * Intercom Provider
 * 
 * Manages Intercom lifecycle and triggers JWT refresh when data changes.
 * All attributes are signed in JWT for verified updates in Intercom.
 */

import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { intercomService } from '@/services/analytics/intercom';
import { authService } from '@/services/auth';

const IntercomContext = createContext(null);

// Store keys that trigger Intercom updates
const SYNC_TRIGGERS = [
  'parsedProfile',
  'companyProfile',  // Raw profile data - triggers on save
  'companyQuestions',
  'customerRequests',
  'saveStatus',
  'regIndustries',  // Triggers when industries list loads (for industry name lookup)
  'users'  // Triggers when user updates personal settings (name, etc.)
];

export const IntercomProvider = ({ children }) => {
  const isInitializedRef = useRef(false);
  const currentUserRef = useRef(null);
  const updateTimeoutRef = useRef(null);
  const unsubscribersRef = useRef([]);

  /**
   * Debounced update handler
   */
  const handleStoreChange = useCallback(() => {
    // Clear existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Debounce updates by 500ms
    updateTimeoutRef.current = setTimeout(() => {
      if (typeof window !== 'undefined' && window.appStore && isInitializedRef.current) {
        intercomService.update(window.appStore);
      }
    }, 500);
  }, []);

  /**
   * Setup store subscriptions
   */
  const setupSubscriptions = useCallback(() => {
    // Clean up existing subscriptions
    unsubscribersRef.current.forEach(fn => fn?.());
    unsubscribersRef.current = [];

    if (typeof window === 'undefined' || !window.appStore) return;

    // Subscribe to relevant store keys
    SYNC_TRIGGERS.forEach(key => {
      const unsub = window.appStore.subscribe(key, handleStoreChange);
      if (unsub) {
        unsubscribersRef.current.push(unsub);
      }
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('[IntercomProvider] Subscribed to store changes:', SYNC_TRIGGERS);
    }
  }, [handleStoreChange]);

  /**
   * Initialize Intercom
   */
  const initialize = useCallback(async () => {
    const authState = authService.getAuthState();
    if (!authState?.authenticated || !authState.user?.email) {
      return false;
    }

    const store = typeof window !== 'undefined' ? window.appStore : null;
    const success = await intercomService.initialize(store);

    if (success) {
      isInitializedRef.current = true;
      currentUserRef.current = authState.user.email;
      setupSubscriptions();

      // Delayed refresh to catch late-loading data
      setTimeout(() => {
        if (typeof window !== 'undefined' && window.appStore) {
          intercomService.update(window.appStore);
        }
      }, 3000);

      return true;
    }

    return false;
  }, [setupSubscriptions]);

  /**
   * Cleanup
   */
  const cleanup = useCallback(() => {
    // Clear subscriptions
    unsubscribersRef.current.forEach(fn => fn?.());
    unsubscribersRef.current = [];

    // Clear pending timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }

    // Shutdown Intercom
    intercomService.shutdown();
    isInitializedRef.current = false;
    currentUserRef.current = null;
  }, []);

  // Initialize on mount
  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 10;

    const tryInitialize = async () => {
      if (!mounted) return;

      const authState = authService.getAuthState();
      if (!authState?.authenticated) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(tryInitialize, Math.min(200 * retryCount, 2000));
        }
        return;
      }

      await initialize();
    };

    tryInitialize();

    return () => {
      mounted = false;
      cleanup();
    };
  }, [initialize, cleanup]);

  // Watch for Intercom banner iframe and manage layout offset dynamically
  useEffect(() => {
    if (typeof document === 'undefined') return;

    let resizeObserver = null;
    let currentBanner = null;

    const updateBannerHeight = (banner) => {
      const h = banner.getBoundingClientRect().height;
      if (h > 0) {
        document.body.classList.add('intercom-banner-active');
        document.documentElement.style.setProperty('--intercom-banner-h', `${h}px`);
      }
    };

    const deactivateBanner = () => {
      document.body.classList.remove('intercom-banner-active');
      document.documentElement.style.setProperty('--intercom-banner-h', '0px');
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      currentBanner = null;
    };

    const checkForBanner = () => {
      const banner = document.querySelector(
        'iframe[name="intercom-banner-frame"], iframe[title*="banner"]'
      );

      if (banner) {
        // Already tracking this banner
        if (banner === currentBanner) return;

        currentBanner = banner;
        updateBannerHeight(banner);

        // Watch for height changes (text reflow, window resize)
        if (resizeObserver) resizeObserver.disconnect();
        resizeObserver = new ResizeObserver(() => updateBannerHeight(banner));
        resizeObserver.observe(banner);
      } else {
        deactivateBanner();
      }
    };

    checkForBanner();

    // Watch for Intercom injecting/removing the banner iframe
    const mutationObserver = new MutationObserver(() => checkForBanner());
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      deactivateBanner();
    };
  }, []);

  // Watch for user changes (logout/switch)
  useEffect(() => {
    const interval = setInterval(() => {
      const authState = authService.getAuthState();
      const newEmail = authState?.user?.email || null;

      // User changed
      if (currentUserRef.current !== newEmail) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[IntercomProvider] User changed:', currentUserRef.current, '->', newEmail);
        }

        cleanup();

        if (authState?.authenticated && newEmail) {
          setTimeout(initialize, 100);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [initialize, cleanup]);

  return (
    <IntercomContext.Provider value={intercomService}>
      {children}
    </IntercomContext.Provider>
  );
};

export const useIntercom = () => useContext(IntercomContext);
export default IntercomProvider;
